import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Event } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Collection reference
const eventsCollection = collection(db, 'events');

// Get all events
export const getEvents = async (): Promise<Event[]> => {
  try {
    // Get today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    const q = query(
      eventsCollection,
      where('date', '>=', todayStr),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Event));
    
    return events;
  } catch (error) {
    console.error('Error getting events:', error);
    return [];
  }
};

// Get featured events
export const getFeaturedEvents = async (city?: string | null): Promise<Event[]> => {
  try {
    // Get today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    let conditions = [
      where('date', '>=', todayStr),
      orderBy('date', 'asc')
    ];

    // Add city filter if provided
    if (city) {
      conditions.unshift(where('city', '==', city));
    }

    const q = query(eventsCollection, ...conditions, limit(4));
    
    const querySnapshot = await getDocs(q);
    const events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Event));
    
    return events;
  } catch (error) {
    console.error('Error getting featured events:', error);
    return [];
  }
};

// Get events by filters
export const getFilteredEvents = async (
  city?: string,
  category?: string,
  date?: string
): Promise<Event[]> => {
  try {
    // Get today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    // Build query conditions
    const conditions = [];

    // Add date condition
    if (date) {
      conditions.push(where('date', '==', date));
    } else {
      conditions.push(where('date', '>=', todayStr));
    }

    // Add category condition if specified
    if (category) {
      conditions.push(where('category', '==', category));
    }

    // Add city condition if specified
    if (city) {
      conditions.push(where('city', '==', city));
    }

    // Add ordering
    conditions.push(orderBy('date', 'asc'));

    // Create and execute the query
    const q = query(eventsCollection, ...conditions);
    
    const querySnapshot = await getDocs(q);
    const events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Event));
    
    return events;
  } catch (error) {
    console.error('Error getting filtered events:', error);
    return [];
  }
};

// Get a single event by ID
export const getEventById = async (id: string): Promise<Event | null> => {
  try {
    const docRef = doc(db, 'events', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Event;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting event:', error);
    return null;
  }
};

// Admin operations below
export const addEvent = async (event: Omit<Event, 'id'>, imageFile: File | null): Promise<string> => {
  try {
    let imageUrl = event.imageUrl;
    
    if (imageFile) {
      try {
        const fileName = `${uuidv4()}-${imageFile.name}`;
        const storageRef = ref(storage, `event-images/${fileName}`);
        const uploadResult = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      } catch (error: any) {
        console.error('Error uploading image:', error);
        throw new Error(`Failed to upload image: ${error.message}`);
      }
    }
    
    // Remove undefined values from event data
    const eventData = Object.fromEntries(
      Object.entries({
        ...event,
        imageUrl,
        createdAt: new Date()
      }).filter(([_, value]) => value !== undefined)
    );
    
    const docRef = await addDoc(eventsCollection, eventData);
    return docRef.id;
  } catch (error) {
    console.error('Error in addEvent:', error);
    throw error;
  }
};

export const updateEvent = async (
  id: string,
  eventData: Partial<Event>,
  imageFile: File | null
): Promise<void> => {
  try {
    const docRef = doc(db, 'events', id);
    let updatedData = { ...eventData };
    
    if (imageFile) {
      try {
        const fileName = `${uuidv4()}-${imageFile.name}`;
        const storageRef = ref(storage, `event-images/${fileName}`);
        const uploadResult = await uploadBytes(storageRef, imageFile);
        updatedData.imageUrl = await getDownloadURL(uploadResult.ref);
      } catch (error: any) {
        console.error('Error uploading image:', error);
        throw new Error(`Failed to upload image: ${error.message}`);
      }
    }
    
    // Remove undefined values from update data
    const cleanedData = Object.fromEntries(
      Object.entries(updatedData).filter(([_, value]) => value !== undefined)
    );
    
    await updateDoc(docRef, cleanedData);
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'events', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};